import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { Controller, useForm } from 'react-hook-form';
import ArrowReloadHorizontalIcon from '@/components/icons/ArrowReloadHorizontalIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useTwoFactorAuthApi } from '@/features/account/hooks/useTwoFactorAuthApi';
import { SetTwoFactorEnabledRequestDto } from '@/features/account/types/account-dto';

const VerifyFormSchema = z.object({
    pin: z.string().length(4, {
        message: 'Verification code must be 4 characters.',
    }),
});

type VerifyFormData = z.infer<typeof VerifyFormSchema>;

type Props = {
    mode?: boolean;
	email?: string;
    onClose: () => void;
    isMobile?: boolean;
};

const ESwitchSecurityModal: FC<PropsWithChildren<Props>> = ({ mode, email, onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [resendTimer, setResendTimer] = useState(60);
    const [token, setToken] = useState('');

    const { t } = useTranslation();

    const { twoFactorAuthCode, setTwoFactorAuth } = useTwoFactorAuthApi({
        send: resendTimer === 60,
    });

    useEffect(() => {
        if (twoFactorAuthCode) {
            setToken(twoFactorAuthCode.token);
        }
    }, [twoFactorAuthCode, setToken]);

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendTimer]);

    const { control, handleSubmit } = useForm<VerifyFormData>({
        resolver: zodResolver(VerifyFormSchema),
        defaultValues: {
            pin: '',
        },
    });

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleResendCode = () => {
        if (resendTimer === 0) {
            setResendTimer(60);
        }
    };

    const submitFormHandler = async (form: VerifyFormData) => {
        const dto: SetTwoFactorEnabledRequestDto = {
            token: token,
            code: form.pin,
            twoFactorEnabled: !mode,
        };

        await setTwoFactorAuth(dto);
        handleClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t(`Turn ${mode ? 'off' : 'on'} 2FA`)}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <DialogDescription>
                        {`To turn ${mode ? 'off' : 'on'} 2FA,
						enter the 4-digit code we sent to ${email}`}
                    </DialogDescription>
                    <Controller
                        name="pin"
                        control={control}
                        render={({ field: { value, onChange }, fieldState, formState }) => (
                            <>
                                <InputOTP
                                    maxLength={4}
                                    value={value}
                                    onChange={onChange}
                                    disabled={formState.disabled}
                                >
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <InputOTPGroup key={index}>
                                            <InputOTPSlot index={index} />
                                        </InputOTPGroup>
                                    ))}
                                </InputOTP>
                                {fieldState.invalid && (
                                    <div className="flex items-center gap-1.5">
                                        <InformationCircle className="size-4 stroke-error-1000" />
                                        <span className="text-xs text-error-1000">
                                            {fieldState.error?.message}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    />
                </div>
                <DialogFooter className={cn('flex-col-reverse', isMobile && 'mt-auto')}>
                    <Button variant="outline" onClick={handleResendCode} disabled={resendTimer > 0}>
                        <ArrowReloadHorizontalIcon
                            className={cn(resendTimer > 0 ? 'stroke-popover-400' : '')}
                        />
                        {resendTimer > 0 ? t(`Resend code in ${resendTimer}s`) : t('Resend code')}
                    </Button>
                    <Button
                        variant={mode ? 'alert' : 'default'}
                        onClick={handleSubmit(submitFormHandler)}
                    >
                        {t(`Turn ${mode ? 'off' : 'on'} 2FA`)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ESwitchSecurityModal;
